<template>
  <div class="payment-detail">
    <div v-if="shouldShowRequirement" class="payment-detail__block">
      <div class="payment-detail-header">Requirements</div>
      <div class="payment-detail-content">
        <ul>
          <li v-for="requirement in requirements" :key="requirement">
            {{ requirement }}
          </li>
        </ul>
      </div>
    </div>
    <div v-if="shouldShowBenefits" class="payment-detail__block">
      <div class="payment-detail-header">Benefits</div>
      <div class="payment-detail-content">
        <ul>
          <li v-for="benefit in benefits" :key="benefit">{{ benefit }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
// ====================================
// Storage
// ====================================
import { watch, computed } from "vue";
import { useGlobalStore } from "@/stores/globalStore";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";
// ====================================
// Composables
// ====================================
import usePaymentFilters from "@/composables/filters/usePaymentFilters";
import {
  addThousandSeperator,
  toCurrencyFormat,
} from "@/composables/helpers/useStringHelpers";

const orderStore = useOrderFormStore();
const { filterPaymentDataByPaymentName, filterPaymentLimit } =
  usePaymentFilters();

const requirements = computed(() => {
  return getPaymentDetails().requirements;
});

const shouldShowRequirement = computed(() => {
  return getPaymentDetails().requirements.length > 0;
});

const benefits = computed(() => {
  return getPaymentDetails().benefits;
});

const shouldShowBenefits = computed(() => {
  return getPaymentDetails().benefits.length > 0;
});

const getPaymentDetails = () => {
  const paymentMethod = filterPaymentDataByPaymentName(
    orderStore.getActiveStorePaymentData,
    orderStore.getActiveStore.getFormPayload.payment
  );
  const transactionLimits = filterPaymentLimit(
    orderStore.getActiveStore.getFiatType,
    orderStore.getActiveStore.getFormPayload.payment,
    orderStore.getActiveStorePaymentData
  );
  // when transaction limit is not defined, the payment method is not selected
  if (paymentMethod.length == 0 || !transactionLimits) {
    return {
      requirements: [],
      benefits: [],
    };
  }

  return {
    requirements: paymentMethod[0].requirements,
    benefits: [
      `Up to ${toCurrencyFormat(
        transactionLimits.weekly,
        transactionLimits.fiat_code
      ).slice(0, -3)} ${transactionLimits.fiat_code} a week`,
      ...paymentMethod[0].benefits,
    ],
  };
};
</script>

<style lang="scss">
.payment-detail {
  background: var(--input-background);
  padding: 0.8rem 2rem 2rem 1.5rem;
  margin-bottom: 3.4rem;
  border-radius: 0rem 0rem 1.6rem 1.6rem;
  display: grid;
  grid-template-columns: 1fr 5fr;
  grid-template-rows: auto;
  // Below css will create background image that looks like a border.
  background-image: linear-gradient(
    rgba(255, 255, 255, 0.07),
    rgba(255, 255, 255, 0.07)
  );
  background-repeat: no-repeat;
  background-size: 90% 1px;
  background-position: center top;

  &__block {
    grid-column-start: 2;
    padding-left: 0.5rem;
  }

  .payment-detail__block:not(:last-child) {
    padding-bottom: 0.5rem;
  }

  .payment-detail-header {
    font-size: 1.25rem;
    font-weight: 500;
    padding-bottom: 0.5rem;
    @include breakpoint($min-s) {
      font-size: $font-body;
    }
  }

  .payment-detail-header:first-child {
    margin-top: 1rem;
  }

  .payment-detail-content {
    line-height: 2;
    font-size: 11px;
    font-style: normal;
    padding-bottom: 0.5rem;
    color: --text-color;
    opacity: 0.6;
    text-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);

    ul {
      list-style: disc;
      padding-left: 20px;
    }
  }
}
</style>
